import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUser, BreakProject, LoginResponse, RoleName } from '@chronos/shared';
import { apiFetch, onUnauthorized, tokenStore, type MeResponse } from '@/api/client';

interface AuthState {
  user: AuthUser | null;
  settings: MeResponse['settings'] | null;
  breakProject: BreakProject | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  applySession: (res: LoginResponse) => void;
  hasRole: (...roles: RoleName[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [settings, setSettings] = useState<MeResponse['settings'] | null>(null);
  const [breakProject, setBreakProject] = useState<BreakProject | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refreshUser = useCallback(async () => {
    if (!tokenStore.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await apiFetch<MeResponse>('/api/auth/me');
      setUser(me.user);
      setSettings(me.settings);
      setBreakProject(me.breakProject ?? null);
    } catch {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  // A refresh failure anywhere in the app drops the session here.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        queryClient.clear();
      }),
    [queryClient],
  );

  const applySession = useCallback((res: LoginResponse) => {
    tokenStore.set(res);
    setUser(res.user);
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await apiFetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: { username, password },
        anonymous: true,
      });
      applySession(res);
      // /auth/me also returns the client-relevant settings.
      void refreshUser();
      return res.user;
    },
    [applySession, refreshUser],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.refresh;
    try {
      if (refreshToken) await apiFetch('/api/auth/logout', { method: 'POST', body: { refreshToken }, anonymous: true });
    } finally {
      tokenStore.clear();
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const hasRole = useCallback((...roles: RoleName[]) => !!user && roles.some((r) => user.roles.includes(r)), [user]);

  const value = useMemo(
    () => ({ user, settings, breakProject, loading, login, logout, refreshUser, applySession, hasRole }),
    [user, settings, breakProject, loading, login, logout, refreshUser, applySession, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
